/**
 * Sign-in.
 *
 * Three ways in, in order of preference:
 *  - Google, rendered by Identity Services so a returning person sees their own
 *    account on the button rather than a generic label.
 *  - An address and a password, for staff without a Google account.
 *  - The dev stub, which exists only in a dev build and only where the server
 *    agrees to expose its endpoint.
 *
 * No password is ever held in this component beyond the keystroke that sent it,
 * and nothing about a failure says which of the address or the password was
 * wrong — the server deliberately does not tell us, so we cannot tell the person.
 */
import { useEffect, useRef, useState, type FormEvent } from 'react';
import { useNavigate, useSearchParams } from 'react-router';
import { ApiError, get, post } from '../api.ts';
import { Petals } from '../components/Hero.tsx';

/**
 * The slice of Google Identity Services this page calls. Declared rather than
 * pulled from a types package: it is four calls, and the library itself is
 * loaded from Google at runtime, so a dependency would only describe it.
 */
interface GoogleIdentity {
  accounts: {
    id: {
      initialize(config: {
        client_id: string;
        nonce: string;
        callback: (response: { credential: string }) => void;
        auto_select: boolean;
        cancel_on_tap_outside: boolean;
        use_fedcm_for_prompt: boolean;
      }): void;
      renderButton(
        parent: HTMLElement,
        options: {
          type: 'standard';
          theme: 'outline';
          size: 'large';
          text: 'signin_with' | 'continue_with';
          shape: 'rectangular';
          logo_alignment: 'left' | 'center';
          locale: string;
          width?: number;
        },
      ): void;
      /** The browser-mediated "welcome back" card for a returning account. */
      prompt(): void;
    };
  };
}
declare global {
  var google: GoogleIdentity | undefined;
}

const GSI_SRC = 'https://accounts.google.com/gsi/client';

/**
 * Load the Identity Services library once per document.
 *
 * The promise is what gets cached, not the <script> tag. Keying on the tag looks
 * equivalent and is not: a second caller finds the element the moment the first
 * one appends it, resolves while the download is still in flight, and then finds
 * no `google` global — which is exactly what a StrictMode double-invoked effect
 * does in development.
 */
let gsiLoad: Promise<void> | null = null;
function loadGsi(): Promise<void> {
  gsiLoad ??= new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.src = GSI_SRC;
    script.async = true;
    script.onload = () => resolve();
    script.onerror = () => {
      gsiLoad = null; // a later attempt may succeed; do not cache the failure
      reject(new Error('gsi unavailable'));
    };
    document.head.append(script);
  });
  return gsiLoad;
}

/**
 * Google's "G", as the official four-colour mark at 18px.
 *
 * Inline rather than fetched: the CSP allows no remote images, and the logo is
 * a brand asset — its colours and proportions are fixed by Google's identity
 * guidelines and must not be restyled to match ours.
 */
function GoogleMark() {
  return (
    <svg width="18" height="18" viewBox="0 0 18 18" aria-hidden focusable="false">
      <path
        fill="#EA4335"
        d="M9 3.48c1.69 0 2.85.73 3.5 1.34l2.56-2.5C13.46.89 11.43 0 9 0 5.48 0 2.44 2.02.96 4.96l2.91 2.26C4.6 5.05 6.62 3.48 9 3.48z"
      />
      <path
        fill="#4285F4"
        d="M17.64 9.2c0-.74-.06-1.28-.19-1.84H9v3.48h4.84c-.1.81-.62 2.03-1.79 2.85l2.84 2.2c1.7-1.57 2.75-3.88 2.75-6.69z"
      />
      <path
        fill="#FBBC05"
        d="M3.88 10.78A5.54 5.54 0 0 1 3.58 9c0-.62.11-1.22.29-1.78L.96 4.96A9.01 9.01 0 0 0 0 9c0 1.45.35 2.82.96 4.04l2.92-2.26z"
      />
      <path
        fill="#34A853"
        d="M9 18c2.43 0 4.47-.8 5.95-2.18l-2.84-2.2c-.76.53-1.78.9-3.11.9-2.38 0-4.4-1.57-5.13-3.74L.96 13.04C2.44 15.98 5.48 18 9 18z"
      />
    </svg>
  );
}

/** Reveal control for the password field. Decorative; the button carries the label. */
function EyeIcon({ off }: { off: boolean }) {
  return (
    <svg
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.7"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
      focusable="false"
    >
      <path d="M2 12s3.6-6.5 10-6.5S22 12 22 12s-3.6 6.5-10 6.5S2 12 2 12z" />
      <circle cx="12" cy="12" r="2.6" />
      {off && <path d="M4 20 20 4" />}
    </svg>
  );
}

/** Reasons the server can bounce a sign-in attempt back to this page. */
const HANDOFF_ERRORS: Record<string, string> = {
  sso_unavailable: 'Googleログインは未設定です。メールアドレスとパスワードをご利用ください。',
};

/**
 * Enter the app as the account that just signed in.
 *
 * A document load, not a client-side navigation, and this is a correctness
 * requirement rather than a preference. Everything the previous principal left
 * behind in this document - the loader cache, the event stream opened with their
 * session, whatever a component is holding in state - dies with the document.
 *
 * Navigating within the app instead kept the old session's stream attached: when
 * the server noticed that session had been revoked it announced the end, and the
 * person who had just signed in was thrown back to this screen a minute later.
 */
const enterApp = (to: string) => {
  window.location.assign(to);
};

export default function Login() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  /*
   * Where the person was when the session ended. A 401 mid-session sends them
   * here with that screen attached, so signing in returns them to it rather
   * than to the dashboard.
   *
   * Same-origin path only: this is read straight off the query string, and
   * `//host` or an absolute URL would turn the login screen into an open
   * redirect.
   */
  const back = searchParams.get('redirect_to');
  const returnTo = back?.startsWith('/') && !back.startsWith('//') ? back : '/';
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [code, setCode] = useState('');
  /** Set when the server paused the sign-in for a mailed code. */
  const [challenge, setChallenge] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  // Dev only. The server refuses this endpoint entirely in production, so the
  // control cannot exist there — but without it a wiped database leaves nobody
  // able to reach the JMC screens.
  //
  // Off by default: a platform operator holds no clinic membership, so ticking
  // this for an account that has one is refused. The common case in development
  // is signing in as a clinic user.
  const [platformAdmin, setPlatformAdmin] = useState(false);
  // The fallback SSO link is a plain anchor, so a failed handoff can only come
  // back as a query parameter — there is no fetch here whose rejection to catch.
  const handoffError = HANDOFF_ERRORS[searchParams.get('error') ?? ''] ?? null;
  const [error, setError] = useState<string | null>(null);
  const [revealed, setRevealed] = useState(false);
  const buttonRef = useRef<HTMLDivElement>(null);
  const [gsiReady, setGsiReady] = useState(false);

  /**
   * Render Google's own button, which shows the signed-in account — their name
   * and picture — because it is drawn by Google against a session this page
   * cannot see, and offer the same account as a "welcome back" card.
   *
   * Every failure path here is deliberate rather than fatal: if the library is
   * blocked, offline, or the browser refuses third-party frames, `gsiReady` stays
   * false and the plain redirect link remains the way in. Sign-in must never
   * depend on a script from another origin loading.
   */
  useEffect(() => {
    let cancelled = false;

    void (async () => {
      try {
        const { clientId, nonce } = await get<{ clientId: string; nonce: string }>(
          '/auth/google/start',
        );
        await loadGsi();
        if (cancelled || !globalThis.google || !buttonRef.current) return;

        globalThis.google.accounts.id.initialize({
          client_id: clientId,
          nonce,
          // Never sign someone in without a press: auto_select would hand out a
          // session on page load, which is not a decision the person made.
          auto_select: false,
          cancel_on_tap_outside: true,
          use_fedcm_for_prompt: true,
          callback: ({ credential }) => {
            void (async () => {
              try {
                await post('/auth/google', { credential });
                enterApp(returnTo);
              } catch (err) {
                setError(err instanceof Error ? err.message : 'ログインできませんでした');
              }
            })();
          },
        });

        globalThis.google.accounts.id.renderButton(buttonRef.current, {
          type: 'standard',
          theme: 'outline',
          size: 'large',
          text: 'continue_with',
          shape: 'rectangular',
          // Groups the mark with the label instead of pinning it to the far edge.
          logo_alignment: 'center',
          locale: 'ja',
          // Google needs a pixel width and caps it at 400. Taking the slot's own
          // width keeps the button inside the card on a narrow phone, where a
          // hard-coded 360 overflows it.
          width: Math.min(400, buttonRef.current.clientWidth || 360),
        });
        setGsiReady(true);

        // The returning-account card. Google decides whether to show it — there
        // is no session here to consult — and rate-limits itself after dismissal.
        globalThis.google.accounts.id.prompt();
      } catch {
        // Left to the fallback link; nothing to tell the person about.
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [navigate]);

  /*
   * One message for every rejection, matching what the server says. A form that
   * distinguishes "no such address" from "wrong password" is a tool for
   * enumerating accounts - and one that distinguishes "wrong code" from "wrong
   * password" says which addresses are worth attacking.
   */
  const REJECTED = '認証に失敗しました。入力内容をご確認のうえ、もう一度お試しください。';

  async function passwordLogin(event: FormEvent) {
    event.preventDefault();
    setError(null);
    setBusy(true);
    try {
      /*
       * Two possible successes: a session (done), or a challenge - the password
       * was right and a code is on its way to the address. Which one comes back
       * is the SERVER's decision, from the deployment's policy; the form does
       * not ask and cannot know, which is what stops it reporting who is an
       * operator.
       */
      const result = await post<{ ok: boolean; challenge?: string }>('/auth/password-login', {
        email,
        password,
      });
      if (result.challenge) {
        setChallenge(result.challenge);
        setPassword('');
        setCode('');
        return;
      }
      enterApp(returnTo);
    } catch {
      setError(REJECTED);
    } finally {
      setBusy(false);
      setPassword('');
      setCode('');
    }
  }

  /** The second half, when a code was mailed. */
  async function submitCode(event: FormEvent) {
    event.preventDefault();
    setError(null);
    setBusy(true);
    try {
      await post('/auth/login-code', { challenge, code: code.trim() });
      enterApp(returnTo);
    } catch {
      setError(REJECTED);
    } finally {
      setBusy(false);
      setCode('');
    }
  }

  async function devLogin(event: FormEvent) {
    event.preventDefault();
    setError(null);
    try {
      await post('/auth/dev-login', { email, platformAdmin });
      enterApp(returnTo);
    } catch (err) {
      // Prefer the server's field detail: for this endpoint the generic message
      // ("The request was rejected.") hides the only thing worth knowing, which
      // is usually that the account belongs to a clinic.
      setError(
        err instanceof ApiError
          ? (err.fields[0] ?? err.message)
          : err instanceof Error
            ? err.message
            : 'ログインできませんでした',
      );
    }
  }

  return (
    <div className="login">
      <div className="login__shell">
        {/*
          The panel itself is decoration - pure CSS, see .login__art - so it is
          hidden from assistive technology. The mark inside it is not described
          either: the brand line beside it already names the application, and a
          second reading of the same name is noise to a screen reader.
        */}
        <div className="login__art" aria-hidden>
          <Petals />

          <div className="login__mark">
            <img src="/logo.svg" alt="" width={220} height={220} />
          </div>
        </div>

        <div className="login__side">
          <div className="login__card">
            <div className="login__head">
              <p className="login__brand">記事咲</p>
              <h1 className="login__greeting">おかえりなさい</h1>
            </div>

            {/*
              The code step REPLACES the form rather than appearing under it.
              The password is already accepted at this point; leaving its boxes
              on screen invites somebody to retype it and start over.
            */}
            {challenge ? (
              <form className="login__form" onSubmit={(e) => void submitCode(e)}>
                <p className="notice" role="status" style={{ marginTop: 0 }}>
                  <strong>{email}</strong> 宛に確認コードを送信しました。メールに記載された
                  コードを入力してください。
                </p>
                <div className="field">
                  <label htmlFor="login-code">確認コード</label>
                  <input
                    id="login-code"
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    // Focus lands here, because it is the only thing to do next.
                    autoFocus
                    required
                    maxLength={12}
                    placeholder="000000"
                    value={code}
                    onChange={(e) => setCode(e.target.value)}
                  />
                </div>
                <button className="btn login__submit" type="submit" disabled={busy}>
                  {busy ? '確認中…' : 'ログイン'}
                </button>
                <button
                  className="btn btn--ghost btn--sm"
                  type="button"
                  onClick={() => {
                    setChallenge(null);
                    setCode('');
                    setError(null);
                  }}
                >
                  最初からやり直す
                </button>
              </form>
            ) : (
            <form className="login__form" onSubmit={(e) => void passwordLogin(e)}>
              <div className="field">
                <label htmlFor="email">メールアドレス</label>
                <input
                  id="email"
                  type="email"
                  // Named so a password manager can fill and save the pair.
                  // Without these people reuse a password they can remember.
                  autoComplete="username"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                />
              </div>
              <div className="field">
                <label htmlFor="password">パスワード</label>
                <div className="login__reveal">
                  <input
                    id="password"
                    type={revealed ? 'text' : 'password'}
                    autoComplete="current-password"
                    required
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                  />
                  <button
                    type="button"
                    onClick={() => setRevealed((shown) => !shown)}
                    aria-label={revealed ? 'パスワードを隠す' : 'パスワードを表示する'}
                    aria-pressed={revealed}
                  >
                    <EyeIcon off={revealed} />
                  </button>
                </div>
              </div>
              <button className="btn login__submit" type="submit" disabled={busy}>
                {busy ? 'ログイン中…' : 'ログイン'}
              </button>
            </form>
            )}

            <div ref={buttonRef} className="gsi" />

            {/*
              Fallback, not a second option: shown only while Google's own button
              is absent. Same destination, without the account on it.
            */}
            {!gsiReady && (
              <a className="btn btn--google" href={`/api/v1/auth/login?redirect_to=${encodeURIComponent(returnTo)}`}>
                <GoogleMark />
                Google でログイン
              </a>
            )}

            {import.meta.env.DEV && (
              <form onSubmit={(e) => void devLogin(e)} className="login__dev">
                <p className="muted small">開発環境専用ログイン（上のメールアドレスを使用）</p>
                <label className="muted small login__devrow">
                  <input
                    type="checkbox"
                    checked={platformAdmin}
                    onChange={(e) => setPlatformAdmin(e.target.checked)}
                  />
                  JMC管理者としてログイン
                </label>
                <button className="btn btn--ghost btn--sm" type="submit">
                  開発ログイン
                </button>
              </form>
            )}

            {(error ?? handoffError) && (
              <p className="alert" role="alert">
                {error ?? handoffError}
              </p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
